import React, { useCallback, useEffect, useState } from 'react';
import { Dam, Plus, Edit2, Trash2, AlertCircle, Search, Filter, XCircle } from 'lucide-react';
import { useDamStore } from '../../../core/services/DamStore';
import { useUserStore } from '../../../core/services/UserStore';
import { useClientStore } from '../../../core/services/ClientStore';
import { useAuthStore } from '../../../core/services/AuthStore';
import ServiceContainer from '../../../core/services/ServiceContainer';
import { IDam, ICreateDamRequest } from '../../../core/interfaces/IUser';
import { Card, CardContent } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Label } from '../../../shared/components/ui/label';
import { Badge } from '../../../shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../shared/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/select';

const serviceContainer = ServiceContainer.getInstance();
const damService = serviceContainer.getDamService();
const userService = serviceContainer.getUserService();
const clientService = serviceContainer.getClientService();

export const DamsPage: React.FC = () => {
  const { user } = useAuthStore();
  const { dams, loading, error, setDams, addDam, updateDam, removeDam, setLoading, setError } = useDamStore();
  const { users, setUsers } = useUserStore();
  const { clients, setClients } = useClientStore();

  const [showForm, setShowForm] = useState(false);
  const [editingDam, setEditingDam] = useState<IDam | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterClientId, setFilterClientId] = useState<string>('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [damToDelete, setDamToDelete] = useState<IDam | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [formData, setFormData] = useState<ICreateDamRequest>({
    name: '',
    description: '',
    clientId: user?.clientId || '',
    userIds: [],
  });

  const isAdmin = !!user?.roles?.includes('admin');
  const canManageDams = !!user?.roles?.some((role) => role === 'admin' || role === 'gestor');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [damsData, usersData, clientsData] = await Promise.all([
        damService.getAllDams(),
        canManageDams ? userService.getAllUsers() : Promise.resolve([]),
        isAdmin ? clientService.getAllClients() : Promise.resolve([]),
      ]);
      setDams(damsData);
      setUsers(usersData as any);
      setClients(clientsData as any);
      setError(null);
    } catch {
      setError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [canManageDams, isAdmin, setClients, setDams, setError, setLoading, setUsers]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEdit = (dam: IDam) => {
    setEditingDam(dam);
    setFormData({
      name: dam.name,
      description: dam.description || '',
      clientId: dam.clientId,
      userIds: dam.userDams?.map((ud) => ud.user.id) || [],
    });
    setShowForm(true);
  };

  const handleDelete = (dam: IDam) => {
    setDamToDelete(dam);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!damToDelete || deleteConfirmText !== 'DELETAR') return;

    try {
      await damService.deleteDam(damToDelete.id);
      removeDam(damToDelete.id);
      setShowDeleteModal(false);
      setDamToDelete(null);
      setDeleteConfirmText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir barragem');
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDamToDelete(null);
    setDeleteConfirmText('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.clientId) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      setLoading(true);
      if (editingDam) {
        // Remove campos proibidos do payload de edição
        const { clientId, userIds, ...allowedFields } = formData;
        const updated = await damService.updateDam(editingDam.id, allowedFields);
        updateDam(editingDam.id, updated);
      } else {
        const created = await damService.createDam(formData);
        addDam(created);
      }
      handleCloseForm();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar barragem');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingDam(null);
    setFormData({ name: '', description: '', clientId: user?.clientId || '', userIds: [] });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterClientId('all');
  };

  const filteredDams = dams.filter((dam) => {
    const matchesSearch = !searchTerm ||
      dam.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dam.description && dam.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesClient = !filterClientId || filterClientId === 'all' || dam.clientId === filterClientId;
    return matchesSearch && matchesClient;
  });

  const handleUserToggle = (userId: string) => {
    setFormData((prev) => ({
      ...prev,
      userIds: prev.userIds.includes(userId)
        ? prev.userIds.filter((id) => id !== userId)
        : [...prev.userIds, userId],
    }));
  };

  const selectedClient = isAdmin ? clients.find((c) => c.id === formData.clientId) : null;
  const clientUsers = isAdmin
    ? (selectedClient ? users.filter((u) => u.clientId === selectedClient.id) : [])
    : users.filter((u) => u.clientId === user?.clientId);

  if (loading && !dams.length) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Carregando barragens...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Dam className="h-8 w-8 text-blue-600" />
            Gerenciamento de Barragens
          </h1>
          <p className="text-slate-600 mt-2">Gerencie barragens e usuários associados</p>
        </div>
        {canManageDams && (
          <Button onClick={() => setShowForm(true)} className="h-11">
            <Plus className="h-5 w-5 mr-2" />
            Nova Barragem
          </Button>
        )}
      </div>

      {!canManageDams && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          Seu perfil possui acesso somente de visualização para barragens.
        </div>
      )}

      {/* Alertas/Erros */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setError(null)}>✕</Button>
        </div>
      )}

      {/* Filtros */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-900">Filtros de Pesquisa</h2>
          </div>
          {(searchTerm || filterClientId) && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="text-slate-600">
              Limpar filtros
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="searchTerm" className="text-sm font-medium text-slate-700">
              Pesquisar
            </Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                id="searchTerm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome ou descrição..."
                className="h-11 pl-10"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="filterClient" className="text-sm font-medium text-slate-700">
              Filtrar por Cliente
            </Label>
            <div className="flex gap-2">
              <Select value={filterClientId} onValueChange={setFilterClientId}>
                <SelectTrigger id="filterClient" className="h-11">
                  <SelectValue placeholder="Todos os clientes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {filterClientId && filterClientId !== 'all' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-11 px-3"
                  onClick={() => setFilterClientId('all')}
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Tabela de Barragens */}
      <Card>
        <CardContent className="p-6">
          {filteredDams.length === 0 ? (
            <div className="text-center py-12">
              <Dam className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">
                {searchTerm || filterClientId ? 'Nenhuma barragem encontrada com os filtros aplicados' : 'Nenhuma barragem cadastrada'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border max-h-[62vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Nome</TableHead>
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Descrição</TableHead>
                    <TableHead className="font-semibold">Usuários</TableHead>
                    <TableHead className="text-right font-semibold">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDams.map((dam) => (
                    <TableRow key={dam.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{dam.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{dam.client?.name || '-'}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{dam.description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{dam.userDams?.length || 0} usuário(s)</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canManageDams && (
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(dam)}>
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(dam)}>
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showForm && canManageDams} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingDam ? (
                <>
                  <Edit2 className="h-5 w-5 text-blue-600" />
                  Editar Barragem
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-blue-600" />
                  Nova Barragem
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">
                Nome da Barragem *
              </Label>
              <div className="relative">
                <Dam className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome da Barragem"
                  className="h-11 pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-slate-700">
                Descrição
              </Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição (opcional)"
                rows={3}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="client" className="text-sm font-medium text-slate-700">
                Cliente *
              </Label>
              {isAdmin ? (
                <>
                  <Select
                    value={formData.clientId}
                    onValueChange={(value) => setFormData({ ...formData, clientId: value, userIds: [] })}
                    disabled={editingDam !== null}
                  >
                    <SelectTrigger id="client" className="h-11">
                      <SelectValue placeholder="Selecione um cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editingDam && (
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Cliente não pode ser alterado após criação
                    </p>
                  )}
                </>
              ) : (
                <Input id="client" value={user?.clientId || ''} disabled className="h-11" />
              )}
            </div>

            {!editingDam && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  Usuários (opcional)
                </Label>
                <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
                  {clientUsers.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">Nenhum usuário disponível para este cliente</p>
                  ) : (
                    <div className="space-y-2">
                      {clientUsers.map((user) => (
                        <label key={user.id} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.userIds.includes(user.id)}
                            onChange={() => handleUserToggle(user.id)}
                            className="rounded border-slate-300"
                          />
                          <span className="text-sm">{user.name} ({user.roles.join(', ')})</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={handleCloseForm} className="h-11">
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="h-11">
                {loading ? 'Salvando...' : editingDam ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle className="text-xl">Deletar Barragem</DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ Esta ação é irreversível!
              </p>
              <p className="text-sm text-red-700">
                Você está prestes a deletar a barragem:{' '}
                <span className="font-semibold">{damToDelete?.name}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-delete" className="text-sm font-medium text-slate-700">
                Para confirmar, digite <span className="font-bold text-red-600">DELETAR</span>:
              </Label>
              <Input
                id="confirm-delete"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Digite DELETAR"
                className="h-11"
                autoComplete="off"
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={cancelDelete} 
              className="h-11"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              disabled={deleteConfirmText !== 'DELETAR' || loading}
              className="h-11 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Deletando...' : 'Deletar Barragem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
